import React, { useState, useEffect } from 'react';
import { CookieSettings } from '../types';
import ToggleSwitch from './ToggleSwitch';

interface CookieSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (settings: CookieSettings) => void;
  onAcceptAll: () => void;
}

const CookieSettingsModal: React.FC<CookieSettingsModalProps> = ({ isOpen, onClose, onSave, onAcceptAll }) => {
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);
  const [marketingEnabled, setMarketingEnabled] = useState(false);

  useEffect(() => {
    if (isOpen) {
      try {
        const storedSettings = localStorage.getItem('cookieConsent');
        if (storedSettings) {
          const settings: CookieSettings = JSON.parse(storedSettings);
          setAnalyticsEnabled(settings.analytics);
          setMarketingEnabled(settings.marketing);
        } else {
          // Default to disabled if no settings are stored yet
          setAnalyticsEnabled(false);
          setMarketingEnabled(false);
        }
      } catch (error) {
        console.error("Failed to parse cookie settings from localStorage", error);
        setAnalyticsEnabled(false);
        setMarketingEnabled(false);
      }
    }
  }, [isOpen]);

  const handleSave = () => {
    onSave({
      necessary: true,
      analytics: analyticsEnabled,
      marketing: marketingEnabled,
    });
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-navy-blue p-8 rounded-lg shadow-2xl w-full max-w-2xl relative text-off-white" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-light-blue hover:text-off-white text-2xl">&times;</button>
        
        <h2 className="text-3xl font-bold mb-2">Cookie Preferences</h2>
        <p className="text-light-blue mb-6">Manage your cookie settings. You can enable or disable different types of cookies below. Necessary cookies cannot be disabled.</p>

        <div className="space-y-6">
          <div className="bg-dark-blue p-4 rounded-lg">
            <h3 className="font-bold text-lg text-off-white mb-1">Strictly Necessary Cookies</h3>
            <p className="text-sm text-light-blue">These cookies are essential for the website to function and cannot be switched off. They are usually only set in response to actions made by you which amount to a request for services, such as setting your privacy preferences, logging in or filling in forms.</p>
          </div>

          <div className="bg-dark-blue p-4 rounded-lg">
             <ToggleSwitch
                id="analytics-cookies"
                label="Performance & Analytics Cookies"
                checked={analyticsEnabled}
                onChange={setAnalyticsEnabled}
              />
            <p className="text-sm text-light-blue mt-2">These cookies allow us to count visits and traffic sources so we can measure and improve the performance of our site. They help us to know which pages are the most and least popular and see how visitors move around the site.</p>
          </div>

          <div className="bg-dark-blue p-4 rounded-lg">
            <ToggleSwitch
                id="marketing-cookies"
                label="Marketing & Targeting Cookies"
                checked={marketingEnabled}
                onChange={setMarketingEnabled}
              />
            <p className="text-sm text-light-blue mt-2">These cookies may be set through our site by our advertising partners. They may be used by those companies to build a profile of your interests and show you relevant adverts on other sites.</p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-end gap-4 mt-8">
          <button
            onClick={handleSave}
            className="w-full sm:w-auto px-6 py-2 rounded-full border border-brand-purple text-brand-purple bg-navy-blue hover:bg-brand-purple/10 transition-colors font-semibold"
          >
            Save my preferences
          </button>
          <button
            onClick={onAcceptAll}
            className="w-full sm:w-auto px-6 py-2 rounded-full bg-brand-purple text-white hover:bg-purple-700 transition-colors font-semibold"
          >
            Accept all
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieSettingsModal;
